
import { useState, useEffect } from 'react';
import { toast } from '@/components/ui/sonner';
import { Courier } from '@/services/types/courierTypes';
import { deleteCourierWithRelatedData } from '@/utils/supabase/courier/courierMutationOperations';
import { fetchAllCouriers } from '@/pages/admin/couriers/services/courierPaginationService';

interface UseCouriersDataResult {
  couriers: Courier[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  fetchCouriersData: () => Promise<void>;
  deleteCourier: (courierId: string) => Promise<boolean>;
  connectionError: string | null;
}

export const useCouriersData = (): UseCouriersDataResult => {
  const [couriers, setCouriers] = useState<Courier[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Получение всех курьеров
  const fetchCouriersData = async () => {
    setLoading(true);
    setError(null);
    setConnectionError(null);

    try {
      console.log('Загрузка всех курьеров...');

      const result = await fetchAllCouriers();
      
      setCouriers(result.couriers);
      setTotalCount(result.totalCount);
      setConnectionError(result.connectionError);

      if (result.connectionError) {
        setError(`Ошибка подключения: ${result.connectionError}`);
      }
    } catch (error: any) {
      console.error('Непредвиденная ошибка при загрузке курьеров:', error);
      setError(`Непредвиденная ошибка: ${error.message}`);
      setConnectionError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Удаление курьера и всех связанных данных
  const deleteCourier = async (courierId: string): Promise<boolean> => {
    try {
      setLoading(true);

      // Используем централизованную функцию для удаления курьера и связанных данных
      const result = await deleteCourierWithRelatedData(courierId);

      if (!result.success) {
        throw new Error(result.error?.message || 'Не удалось удалить курьера');
      }

      // Удаляем курьера из локального состояния
      setCouriers(prev => prev.filter(c => c.id !== courierId));
      
      // Обновляем общее количество
      setTotalCount(prev => prev - 1);
      
      toast.success('Курьер и все связанные данные успешно удалены');
      return true;
    } catch (error: any) {
      console.error('Ошибка при удалении курьера:', error);
      toast.error(`Не удалось удалить курьера: ${error.message}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // При монтировании компонента загружаем все данные
    fetchCouriersData();
  }, []);

  return {
    couriers,
    loading,
    error,
    totalCount,
    fetchCouriersData,
    deleteCourier,
    connectionError,
  };
};

// Экспорт по умолчанию для обратной совместимости
export default useCouriersData;
